import { useMutation, useQueryClient } from '@tanstack/react-query';
import { type ApiError, apiFetch } from '@/lib/api/client';
import { useToast } from '@/components/ui/toast';
import { type ApiInvestmentResponse, parseDecimalInput } from './types';
import { INVESTMENTS_QUERY_KEY } from './useInvestments';

/** Formulário de novo aporte (follow-on). */
export interface RoundForm {
  /** Valor do aporte em BRL, digitado (ex.: "50.000.000" ou "50000000"). */
  amountBRL: string;
  /** Data do aporte (yyyy-MM-dd do input date). */
  deployedAt: string;
}

export interface RoundBody {
  amountBRL: number;
  deployedAtUtc: string;
}

/** True se o aporte tem valor positivo e data — habilita o confirmar. */
export function isRoundValid(form: RoundForm): boolean {
  const amount = parseDecimalInput(form.amountBRL);
  return amount != null && amount > 0 && form.deployedAt.trim() !== '';
}

/**
 * Monta o corpo do POST /rounds (função pura). O valor é parseado em pt-BR; a data do input
 * (yyyy-MM-dd) vira ISO em UTC. Assume validação prévia (isRoundValid) — amount não-positivo vira 0.
 */
export function buildRoundBody(form: RoundForm): RoundBody {
  const amount = parseDecimalInput(form.amountBRL);
  return {
    amountBRL: amount != null && amount > 0 ? amount : 0,
    deployedAtUtc: new Date(`${form.deployedAt}T00:00:00Z`).toISOString(),
  };
}

/**
 * Adiciona uma rodada de aporte: POST /api/investments/{id}/rounds com If-Match (ETag da ficha).
 * O backend incrementa o cheque total. Em sucesso, invalida a ficha e a lista. 412 → aviso + refetch.
 */
export function useAddInvestmentRound() {
  const qc = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: ({ id, etag, body }: { id: string; etag: string; body: RoundBody }) =>
      apiFetch<ApiInvestmentResponse>(`/api/investments/${id}/rounds`, {
        method: 'POST',
        headers: { 'If-Match': `"${etag}"` },
        body,
      }),
    onSuccess: (resp) => {
      void qc.invalidateQueries({ queryKey: ['investment', resp.id] });
      void qc.invalidateQueries({ queryKey: INVESTMENTS_QUERY_KEY });
      toast(`Aporte registrado em ${resp.codename}.`);
    },
    onError: (err, vars) => {
      if ((err as ApiError).status === 412) {
        toast('Esse investimento foi alterado por outra pessoa. Atualizei a ficha.', 'warn');
        void qc.invalidateQueries({ queryKey: ['investment', vars.id] });
      } else {
        toast('Não foi possível registrar o aporte. Tente de novo.', 'danger');
      }
    },
  });
}
